import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ApiClient } from '@/infrastructure/api/api-client';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { UnsavedChangesBar } from '@/shared/ui/unsaved-changes-bar';

/**
 * Third-party services the editor talks to that are not AI chat providers.
 *
 * Chat providers moved to the AI hub, where their keys sit next to the models
 * and prices they govern. What is left — stock footage, avatars, B-roll,
 * transcription — has nothing to do with model pricing and was cluttering a
 * settings page that is otherwise about platform behaviour.
 */

interface ConnectorSettings {
  pexels_api_key: string | null;
  pexels_api_key_set: boolean;
  wavespeed_api_key: string | null;
  wavespeed_api_key_set: boolean;
  heygen_api_key: string | null;
  heygen_api_key_set: boolean;
  json2video_api_key: string | null;
  json2video_api_key_set: boolean;
}

function ConnectorsPage() {
  const [settings, setSettings] = useState<ConnectorSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [pexelsKey, setPexelsKey] = useState('');
  const [wavespeedKey, setWavespeedKey] = useState('');
  const [heygenKey, setHeygenKey] = useState('');
  const [json2videoKey, setJson2videoKey] = useState('');

  const reload = async () => {
    const data = await ApiClient.get<ConnectorSettings>('/admin/settings');
    setSettings(data);
  };

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, []);

  const dirty = Boolean(pexelsKey || wavespeedKey || heygenKey || json2videoKey);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, string> = {};
      if (pexelsKey) payload.pexels_api_key = pexelsKey;
      if (wavespeedKey) payload.wavespeed_api_key = wavespeedKey;
      if (heygenKey) payload.heygen_api_key = heygenKey;
      if (json2videoKey) payload.json2video_api_key = json2videoKey;

      await ApiClient.put('/admin/settings', payload);
      await reload();
      setPexelsKey('');
      setWavespeedKey('');
      setHeygenKey('');
      setJson2videoKey('');
      setMessage('Saved');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Saving ignores blank fields on purpose, so clearing an input cannot remove
   * a key. This asks the server to drop the row instead.
   */
  const removeKey = async (key: string) => {
    if (!confirm(`Remove ${key.replace(/_/g, ' ')}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await ApiClient.delete(`/admin/settings/key?key=${encodeURIComponent(key)}`);
      await reload();
      setMessage('Removed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-zinc-400">Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Connectors</h1>
        <p className="mt-1 text-sm text-zinc-400">
          Third-party services the editor uses. AI chat providers live under{' '}
          <span className="text-zinc-200">AI Providers</span>, next to their pricing.
        </p>
      </div>

      {message && (
        <div className="mb-4 rounded-lg border border-green-800 bg-green-950 px-4 py-2 text-sm text-green-300">
          {message}
        </div>
      )}

      <div className="space-y-6">
        {/* Pexels API Key */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
          <h2 className="mb-1 text-lg font-semibold text-white">
            Pexels API Key
          </h2>
          <p className="mb-4 text-sm text-zinc-400">
            Used for stock video/image search in the editor.
          </p>

          {settings?.pexels_api_key_set && (
            <div className="mb-3 flex items-center gap-2">
              <span className="rounded bg-green-900 px-2 py-1 text-xs text-green-300">
                Set
              </span>
              <span className="font-mono text-sm text-zinc-400">
                {settings.pexels_api_key}
              </span>
              <button
                type="button"
                onClick={() => removeKey('pexels_api_key')}
                className="ml-auto rounded px-2 py-1 text-xs text-red-400 hover:bg-red-950 hover:text-red-300"
              >
                Remove
              </button>
            </div>
          )}

          <input
            type="password"
            placeholder={
              settings?.pexels_api_key_set
                ? 'Enter new key to replace...'
                : 'Enter Pexels API key...'
            }
            value={pexelsKey}
            onChange={(e) => setPexelsKey(e.target.value)}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none"
          />
        </div>

        {/* WaveSpeed API Key (AI B-Roll Generation) */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
          <h2 className="mb-1 text-lg font-semibold text-white">
            WaveSpeed API Key
          </h2>
          <p className="mb-4 text-sm text-zinc-400">
            Used for AI-powered B-Roll video generation (Seedance model).
            Get your key from wavespeed.ai
          </p>

          {settings?.wavespeed_api_key_set && (
            <div className="mb-3 flex items-center gap-2">
              <span className="rounded bg-green-900 px-2 py-1 text-xs text-green-300">
                Set
              </span>
              <span className="font-mono text-sm text-zinc-400">
                {settings.wavespeed_api_key}
              </span>
              <button
                type="button"
                onClick={() => removeKey('wavespeed_api_key')}
                className="ml-auto rounded px-2 py-1 text-xs text-red-400 hover:bg-red-950 hover:text-red-300"
              >
                Remove
              </button>
            </div>
          )}

          <input
            type="password"
            placeholder={
              settings?.wavespeed_api_key_set
                ? 'Enter new key to replace...'
                : 'Enter WaveSpeed API key...'
            }
            value={wavespeedKey}
            onChange={(e) => setWavespeedKey(e.target.value)}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none"
          />
        </div>

        {/* HeyGen API Key */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
          <h2 className="mb-1 text-lg font-semibold text-white">
            HeyGen API Key
          </h2>
          <p className="mb-4 text-sm text-zinc-400">
            Used for AI avatar video generation, video translation, and lip-sync.
            Get your key from app.heygen.com/settings
          </p>

          {settings?.heygen_api_key_set && (
            <div className="mb-3 flex items-center gap-2">
              <span className="rounded bg-green-900 px-2 py-1 text-xs text-green-300">
                Set
              </span>
              <span className="font-mono text-sm text-zinc-400">
                {settings.heygen_api_key}
              </span>
              <button
                type="button"
                onClick={() => removeKey('heygen_api_key')}
                className="ml-auto rounded px-2 py-1 text-xs text-red-400 hover:bg-red-950 hover:text-red-300"
              >
                Remove
              </button>
            </div>
          )}

          <input
            type="password"
            placeholder={
              settings?.heygen_api_key_set
                ? 'Enter new key to replace...'
                : 'Enter HeyGen API key...'
            }
            value={heygenKey}
            onChange={(e) => setHeygenKey(e.target.value)}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none"
          />
        </div>

        {/* json2video API Key (transcription) */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
          <h2 className="mb-1 text-lg font-semibold text-white">
            json2video API Key
          </h2>
          <p className="mb-4 text-sm text-zinc-400">
            Used for media transcription (subtitles / SRT generation).
            The backend proxies transcription requests to the json2video
            server using this key.
          </p>

          {settings?.json2video_api_key_set && (
            <div className="mb-3 flex items-center gap-2">
              <span className="rounded bg-green-900 px-2 py-1 text-xs text-green-300">
                Set
              </span>
              <span className="font-mono text-sm text-zinc-400">
                {settings.json2video_api_key}
              </span>
              <button
                type="button"
                onClick={() => removeKey('json2video_api_key')}
                className="ml-auto rounded px-2 py-1 text-xs text-red-400 hover:bg-red-950 hover:text-red-300"
              >
                Remove
              </button>
            </div>
          )}

          <input
            type="password"
            placeholder={
              settings?.json2video_api_key_set
                ? 'Enter new key to replace...'
                : 'j2v_...'
            }
            value={json2videoKey}
            onChange={(e) => setJson2videoKey(e.target.value)}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>

      <UnsavedChangesBar
        dirty={dirty}
        saving={saving}
        onSave={handleSave}
        onDiscard={() => {
          setPexelsKey('');
          setWavespeedKey('');
          setHeygenKey('');
          setJson2videoKey('');
        }}
      />
    </div>
  );
}

export const Route = createFileRoute('/admin/connectors')({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (!user?.is_admin) throw new Error('Admin access required');
  },
  component: ConnectorsPage,
});
